import dayjs from "lib/dayjs";
import {
  filter,
  find,
  get,
  includes,
  map,
  orderBy,
  reduce,
  some,
  valuesIn,
} from "lodash";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { NumericFormat } from "react-number-format";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Divider,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";

import { toastError } from "utils/toast.util";
import { useIsTab } from "hooks/is-tab.hook";
import { useIsMobile } from "hooks/is-mobile.hook";
import { useGroupSubscription, useUserData } from "features/User/hooks";

import FormikTextField from "components/base/FormikTextField";
import FormikSelectField from "components/base/FormikSelectField";
import FormikCheckBox from "components/base/FormikCheckBox";

import { prorationMonths } from "features/User/data/subscriptions.data";
import { LicenseLevelRow } from "..";
import { useGeoData } from "features/GeoData";
import { codeFontFamily } from "utils/code-font";

function handleEnterKey(event, callback) {
  if (event.key === "Enter") {
    event.preventDefault();
    callback();
  }
}

const PRIORITY_COUNTRIES = ["USA"];

export default function GroupForm({
  values,
  errors,
  handleChange,
  handleBlur,
  handleSubmit,
  isSubmitting,
  isValid,
  dirty,
  touched,
  setFieldValue,
  setFieldTouched,
  loading,
  products,
  checkPromoCode,
  group,
  initialSubLevel,
  coupons,
  referralCode,
  isTrialUser = false,
}) {
  const {
    email,
    fullName,
    organizationName,
    referenceNo,
    phoneNumber,
    city,
    state,
    country,
    planTypeData,
    isProrated,
    prorateMonth,
    paymentType,
    promoCode,
    couponId,
    productId,
  } = values;

  const isMobile = useIsMobile();
  const isTab = useIsTab();
  const { priceData, updatePriceData } = useGroupSubscription();
  const { countries, activeRegions, updateActiveRegions } = useGeoData();
  const { availableCredit, isReferralHidden } = useUserData();

  // A renewal is when the group already has an active (non-expired) subscription
  const isRenewal = useMemo(() => {
    if (group == null || group.expiration == null) return false;
    return dayjs(group.expiration).isAfter(dayjs());
  }, [group]);

  const [subLevel, setSubLevel] = useState(initialSubLevel);
  const [activeProduct, setActiveProduct] = useState(null);
  const [activeCoupon, setActiveCoupon] = useState(null);
  const [promoError, setPromoError] = useState(null);
  const [isReferralCode, setIsReferralCode] = useState(false);



  const regions = useMemo(() => {
    return orderBy(
      map(activeRegions, (region) => ({
        key: region.state_code,
        label: region.name,
      })),
      ["label"]
    );
  }, [activeRegions]);

  const activePromoCode = useMemo(() => {
    if (activeCoupon == null) {
      return null;
    }
    return find(
      activeCoupon.promoCodes,
      (promo) => promo.code === promoCode.toUpperCase()
    );
  }, [activeCoupon, promoCode]);

  const credits = useMemo(() => {
    const k5Level = find(planTypeData, ["key", "group-k5"]);
    if (
      k5Level != null &&
      k5Level.noOfLicenses != null &&
      k5Level.noOfLicenses !== ""
    ) {
      const totalOtherSeats = reduce(
        filter(planTypeData, (level) => level.key !== "group-k5"),
        (sum, level) => {
          const seats =
            level.noOfLicenses != null && level.noOfLicenses !== ""
              ? level.noOfLicenses
              : 0;
          if (seats < 0) {
            return sum + 0;
          }
          return sum + seats;
        },
        0
      );
      return totalOtherSeats > k5Level.noOfLicenses
        ? k5Level.noOfLicenses < 0
          ? 0
          : k5Level.noOfLicenses
        : totalOtherSeats;
    }
    return 0;
  }, [planTypeData]);

  const totalSeats = useMemo(() => {
    return reduce(
      planTypeData,
      (sum, level) => {
        return (
          sum +
          (level.noOfLicenses === "" || level.noOfLicenses == null
            ? 0
            : level.noOfLicenses)
        );
      },
      0
    );
  }, [planTypeData]);

  const currentMonth = useMemo(() => dayjs().month(), []);

  const prorationPercentage = useMemo(() => {
    if (!isProrated) {
      return 1;
    }
    const currentDay = dayjs().date();
    let numMonthsInProration;
    if (prorateMonth > currentMonth) {
      // end month is this year
      numMonthsInProration = prorateMonth - currentMonth;
    } else {
      // end month is next year
      numMonthsInProration = 11 - currentMonth + (prorateMonth + 1);
    }
    if (currentDay <= 15) {
      numMonthsInProration++;
    }
    const percentage = numMonthsInProration / 12;
    return percentage;
  }, [isProrated, prorateMonth, currentMonth]);

  function handleClearCoupon() {
    setActiveCoupon(null);
    setPromoError(null);
    setFieldValue("couponId", null);
    setFieldValue(
      "promoCode",
      isTrialUser && !isReferralHidden ? referralCode || "" : ""
    );
    setIsReferralCode(false);
  }

  const handleApplyCoupon = useCallback(
    async (defaultCoupon = false) => {
      if (activeProduct == null) {
        toastError("Oops. Something went wrong!");
        return;
      }
      if (promoCode.trim() === "" && !defaultCoupon) {
        setPromoError("Must enter a promo code.");
        return;
      }

      try {
        const coupon = await checkPromoCode(
          activeProduct.id,
          promoCode.trim().toUpperCase()
        );

        if (coupon != null) {
          setActiveCoupon(coupon);
          setPromoError(null);
          setFieldValue("couponId", coupon.id);
          setFieldTouched("couponId");
        } else {
          setPromoError("Not a valid promo code.");
        }
      } catch (error) {
        let msg = "Not a valid promo code.";
        if (error?.response?.data) {
          if (typeof error.response.data === "string") {
            msg = error.response.data;
          } else if (error.response.data.message) {
            msg = error.response.data.message;
          }
        }
        setPromoError(msg);
        setActiveCoupon(null);
        setFieldValue("couponId", null);
        setIsReferralCode(false);
      }
    },
    [activeProduct, promoCode, checkPromoCode, setFieldTouched, setFieldValue]
  );

  useEffect(() => {
    async function fetchRegions() {
      const countryObj = find(countries, { iso3: country });
      if (countryObj != null) {
        await updateActiveRegions(countryObj.id);
      }
    }

    fetchRegions();
  }, [countries, country]);

  useEffect(() => {
    setTimeout(() => {
      if (
        (group != null &&
          !(
            group.contact.country === country && group.contact.state === state
          ) &&
          !some(activeRegions, { state_code: state })) ||
        (group == null && !some(activeRegions, { state_code: state }))
      ) {
        setFieldValue("state", "");
      }
    }, 0);
  }, [
    regions,
    activeRegions,
    country,
    group,
    state,
    setFieldTouched,
    setFieldValue,
  ]);

  useEffect(() => {
    if (
      activeProduct != null &&
      promoCode !== "" &&
      couponId != null &&
      activeCoupon == null
    ) {
      handleApplyCoupon();
    }
  }, [promoCode, couponId, activeProduct, activeCoupon, handleApplyCoupon]);

  useEffect(() => {
    async function findDefaultCouponOrReferral() {
      if (activeProduct == null) {
        return;
      }

      if (isTrialUser && !isReferralHidden && referralCode) {
        const validatedCoupon = await checkPromoCode(
          activeProduct.id,
          referralCode
        );

        if (validatedCoupon) {
          const matchedCode = find(
            validatedCoupon.promoCodes,
            (promo) => promo.code === referralCode.toUpperCase()
          );

          setFieldValue(
            "promoCode",
            matchedCode ? matchedCode.code : referralCode
          );
          setIsReferralCode(true);
          setActiveCoupon(validatedCoupon);
          setPromoError(null);
          setFieldValue("couponId", validatedCoupon.id);
        } else {
          handleClearCoupon();
        }
        return;
      }

      const defaultCoupon = find(
        orderBy(coupons, ["percentageOff"], ["desc"]),
        (coupon) =>
          coupon.isForProduct(activeProduct.id) && totalSeats > coupon.minLimit
      );
      if (defaultCoupon != null) {
        if (
          activeCoupon != null &&
          (!activeCoupon.isDefault || defaultCoupon.id === activeCoupon.id)
        ) {
          return;
        }
        const defaultCode = find(
          defaultCoupon.promoCodes,
          (code) => code.isDefault
        );
        if (defaultCode) {
          const checkedCoupon = await checkPromoCode(
            activeProduct.id,
            defaultCode.code
          );
          if (checkedCoupon != null) {
            setActiveCoupon({ ...checkedCoupon, isDefault: true });
            setPromoError(null);
            setFieldValue("promoCode", defaultCode.code);
            setFieldValue("couponId", checkedCoupon.id);
            setIsReferralCode(false);
          } else {
            handleClearCoupon();
          }
        }
      } else if (activeCoupon != null && activeCoupon.isDefault) {
        handleClearCoupon();
      }
    }
    findDefaultCouponOrReferral();
  }, [
    coupons,
    activeProduct,
    totalSeats,
    referralCode,
    setFieldValue,
    isTrialUser,
    isReferralHidden,
  ]);

  useEffect(() => {
    if (
      products == null ||
      (planTypeData.length > 1 && planTypeData[0].groupPlan === subLevel)
    ) {
      return;
    }
    const planTypeOptions = map(
      filter(
        products,
        (product) =>
          product.metadata.groupPlan != null &&
          product.metadata.groupPlan.toLowerCase() === subLevel
      ),
      (product) => {
        const dataPoint = find(planTypeData, {
          key: product.metadata.licenseLevelKey,
        });
        return {
          key: product.metadata.licenseLevelKey,
          classGroupId: Number(product.metadata.classGroupId),
          label: product.name,
          productId: product.id,
          groupPlan: product.metadata.groupPlan,
          noOfLicenses:
            dataPoint != null && dataPoint.noOfLicenses > 0
              ? dataPoint.noOfLicenses
              : 0,
        };
      }
    );
    const sorted = orderBy(planTypeOptions, ["classGroupId"], ["asc"]);
    setFieldValue("planTypeData", [...sorted]);
    setFieldTouched("planTypeData", false);
  }, [subLevel, products, setFieldTouched, setFieldValue]);

  useEffect(() => {
    const planType = `group-${subLevel}-yearly-nosub`;
    const changedProduct = find(
      products,
      (product) =>
        product.metadata.planType &&
        product.metadata.planType.toLowerCase() === planType.toLowerCase()
    );
    if (changedProduct != null) {
      setActiveProduct(changedProduct);
      setFieldValue("productId", changedProduct.id, true);
    }
  }, [subLevel, products, setFieldValue]);

  useEffect(() => {
    updatePriceData(
      planTypeData,
      couponId,
      isProrated ? prorateMonth : null
    );
  }, [
    planTypeData,
    credits,
    isProrated,
    prorateMonth,
    couponId,
    isReferralCode,
    promoCode,
  ]);

  const referralMessage = useMemo(() => {
    if (isReferralHidden || availableCredit <= 0) return null;

    const discountValue = priceData.discount / 100;

    if (discountValue > 0) {
      if (discountValue >= availableCredit) {
        return "Discounts and referral credit are not stackable. We will apply the higher discount at checkout.";
      } else {
        return "Discounts and referral credit are not stackable. We will apply the higher referral credit at checkout.";
      }
    } else {
      return "We will apply your available referral credit at checkout.";
    }
  }, [availableCredit, priceData.discount, isReferralHidden]);

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        display: "grid",
        gridTemplateColumns: isTab ? "1fr" : "1fr 25vw",
      }}
    >
      <Stack direction="column" mb={2}>
        <Alert severity="info">
          <Typography>
            You will become the first Administrator for this Group Plan. You can
            assign additional Administrators if you choose once your purchase
            has been completed by following the steps below.
          </Typography>
          <Typography>
            After your purchase has been completed, you will be able to start
            inviting users to join your group
          </Typography>
        </Alert>

        <Typography variant="subtitle1">Group Details</Typography>
        <FormikTextField
          fullWidth
          error={get(errors, "organizationName")}
          touched={get(touched, "organizationName", null)}
          handleBlur={handleBlur}
          handleChange={handleChange}
          value={organizationName}
          label="Group Name"
          name="organizationName"
          helperText={isRenewal ? "Group name cannot be changed while renewing." : "What's the best way to describe your group?"}
          disabled={isRenewal}
        />
        <FormikTextField
          fullWidth
          error={get(errors, "referenceNo")}
          touched={get(touched, "referenceNo", null)}
          handleBlur={handleBlur}
          handleChange={handleChange}
          value={referenceNo}
          label="Purchase Order Number (Optional)"
          name="referenceNo"
        />
        <Typography variant="subtitle1">Payment Contact Information</Typography>
        <Typography variant="caption">
          This is the primary billing point of contact. This individual will not
          be an Administrator unless you choose to make them one after your
          purchase has been completed
        </Typography>
        <Typography variant="caption">
          If you are paying via invoice, you will be given the option to include
          additional invoice recipients further below
        </Typography>
        <FormikTextField
          fullWidth
          error={get(errors, "fullName")}
          touched={get(touched, "fullName", null)}
          handleBlur={handleBlur}
          handleChange={handleChange}
          value={fullName}
          label="Contact Name"
          name="fullName"
        />
        <FormikTextField
          fullWidth
          error={get(errors, "email")}
          touched={get(touched, "email", null)}
          handleBlur={handleBlur}
          handleChange={handleChange}
          value={email}
          label="Email Address"
          name="email"
        />
        <FormikTextField
          fullWidth
          error={get(errors, "phoneNumber")}
          touched={get(touched, "phoneNumber", null)}
          handleBlur={handleBlur}
          handleChange={handleChange}
          value={phoneNumber}
          label="Phone Number"
          name="phoneNumber"
        />
        <FormikSelectField
          error={get(errors, "country")}
          handleBlur={handleBlur}
          handleChange={handleChange}
          touched={get(touched, "country", null)}
          value={country}
          label="Country"
          name="country"
          id="country-select"
          size="small"
          options={map(countries, (country) => ({
            key: country.iso3,
            label: `${country.emoji} ${country.name} ${country.native != null &&
              country.name.toLowerCase() !== country.native.toLowerCase()
              ? `(${country.native})`
              : ""
              }`,
            default: country.iso3 === "USA",
          })).sort((countryA, countryB) => {
            const aPriority = includes(PRIORITY_COUNTRIES, countryA.key);
            const bPriority = includes(PRIORITY_COUNTRIES, countryB.key);
            if (aPriority && bPriority) {
              return countryA.name - countryB.name;
            }
            if (aPriority) {
              return -1;
            }
            if (bPriority) {
              return 1;
            }
            return countryA.name - countryB.name;
          })}
        />
        <Stack direction="row" gap={4}>
          <FormikTextField
            error={get(errors, "city")}
            touched={get(touched, "city", null)}
            handleBlur={handleBlur}
            handleChange={handleChange}
            value={city}
            label="City"
            name="city"
            sx={{
              flex: 1,
            }}
          />
          <FormikSelectField
            error={get(errors, "state")}
            handleBlur={handleBlur}
            handleChange={handleChange}
            touched={get(touched, "state", null)}
            value={state}
            label="State/Region"
            name="state"
            id="state-select"
            options={regions}
            size="small"
            sx={{
              flex: 1,
            }}
            disabled={regions.length < 1}
            helperText={
              regions.length > 1 ? null : "Country has no states/regions"
            }
          />
        </Stack>
        <Typography variant="subtitle1">Group Licenses</Typography>
        <Typography variant="caption">
          Please select the appropriate group plan below (K-12, Higher Ed, or
          Gov / Public Library)
        </Typography>
        <Typography variant="caption">
          Populate the required field(s) with the number of licenses you are
          purchasing (1 user = 1 license)
        </Typography>
        <Typography variant="caption">
          There is no price difference between students and staff. Please
          include all users in your numbers below
        </Typography>
        <Typography variant="caption">
          Don’t stress about exact numbers; all Group Plans will be allowed 10%
          user overage with zero penalty
        </Typography>
        <Typography variant="caption">
          K-5 purchases qualify for user credits on a one-for-one basis for
          subscriptions that also include licenses for Grades 6-12
        </Typography>
        <ToggleButtonGroup
          fullWidth
          value={subLevel}
          onChange={(event, value) => {
            if (value != null) {
              setSubLevel(value);
            }
          }}
          exclusive
          disabled={group != null}
        >
          <ToggleButton value="k12">K-12</ToggleButton>
          <ToggleButton value="highed">Higher Ed</ToggleButton>
          <ToggleButton value="gov">Gov</ToggleButton>
        </ToggleButtonGroup>
        <Stack direction="row" alignItems="center">
          <Box sx={{ flex: isMobile ? 6 : 8 }} />
          <Typography
            variant="caption"
            sx={{ flex: isMobile ? 2 : 1 }}
            align="right"
          >
            Per User
          </Typography>
          <Typography
            variant="caption"
            sx={{ flex: isMobile ? 4 : 3 }}
            align="right"
          >
            Total
          </Typography>
        </Stack>
        {planTypeData.map((level, index) => (
          <LicenseLevelRow
            planType={level}
            index={index}
            priceData={priceData.groupPrices}
            credits={credits}
            prorationPercentage={prorationPercentage}
            errors={errors}
            touched={touched}
            handleBlur={handleBlur}
            handleChange={handleChange}
            key={level.key}
          />
        ))}
        {typeof get(errors, "planTypeData") === "string" &&
          !!get(touched, "planTypeData", false) && (
            <Alert variant="outlined" severity="error">
              {`${get(errors, "planTypeData")}`}
            </Alert>
          )}
        <Divider />
        <Stack direction="row" alignItems="center">
          <Box sx={{ flex: 8 }} />
          <Typography variant="caption" sx={{ flex: 1 }} align="right">
            Subtotal:
          </Typography>
          <Typography align="right" sx={{ flex: 3 }}>
            <NumericFormat
              value={priceData.subtotal / 100}
              decimalScale={2}
              prefix="$"
              defaultValue="-"
              displayType="text"
              fixedDecimalScale={true}
              thousandSeparator=","
              thousandsGroupStyle="thousand"
            />
          </Typography>
        </Stack>
        {!isRenewal && (
          <Stack direction="row" alignItems="center">
            <FormikCheckBox
              handleBlur={handleBlur}
              handleChange={handleChange}
              value={isProrated}
              name="isProrated"
              error={get(errors, "isProrated")}
              touched={get(touched, "isProrated", null)}
              label="Yes, prorate my subscription"
              sx={{
                flex: 8,
              }}
            />
            <FormikSelectField
              error={get(errors, "prorateMonth")}
              handleBlur={handleBlur}
              handleChange={handleChange}
              touched={get(touched, "prorateMonth", null)}
              value={prorateMonth}
              label="End Month"
              name="prorateMonth"
              id="prorateMonth-select"
              disabled={!isProrated}
              options={filter(
                prorationMonths,
                (month) => month.key !== currentMonth
              )}
              size="small"
              sx={{
                flex: 4,
              }}
            />
          </Stack>
        )}
        {isReferralCode || (activeCoupon != null && activePromoCode != null) ? (
          <Fragment>
            <Alert
              action={
                <Button
                  color="inherit"
                  variant="text"
                  onClick={handleClearCoupon}
                >
                  CLEAR
                </Button>
              }
            >
              <AlertTitle>
                <Box
                  component="span"
                  sx={{
                    fontFamily: codeFontFamily,
                    letterSpacing: "0.04em",
                  }}
                >
                  {isReferralCode ? promoCode : activePromoCode?.code}
                </Box>
              </AlertTitle>
              <Typography>
                {isReferralCode
                  ? isReferralHidden
                    ? "Discount applied"
                    : "Referral discount applied"
                  : activePromoCode?.description}
              </Typography>
            </Alert>
            <Stack direction="row" alignItems="center">
              <Box sx={{ flex: 8 }} />
              <Typography variant="caption" sx={{ flex: 1 }} align="right">
                Discount:
              </Typography>
              <Typography align="right" color="error.main" sx={{ flex: 3 }}>
                <NumericFormat
                  value={priceData.discount / 100}
                  decimalScale={2}
                  prefix="$-"
                  defaultValue="-"
                  displayType="text"
                  fixedDecimalScale={true}
                  thousandSeparator=","
                  thousandsGroupStyle="thousand"
                />
              </Typography>
            </Stack>
            {referralMessage && (
              <Box sx={{ display: "flex", mt: 0.5 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                >
                  {referralMessage}
                </Typography>
              </Box>
            )}
          </Fragment>
        ) : (
          <Stack direction="row" alignItems="flex-start">
            <TextField
              value={promoCode}
              onChange={(event) => {
                setFieldValue("promoCode", event.target.value);
                if (event.target.value === "") {
                  setPromoError(null);
                }
              }}
              fullWidth
              label={isReferralHidden ? "Promo Code" : "Promo or Referral Code"}
              helperText={promoError}
              error={promoError != null}
              onKeyDown={(event) => handleEnterKey(event, handleApplyCoupon)}
              sx={{
                "& input": {
                  fontFamily: codeFontFamily,
                  letterSpacing: "0.04em",
                },
              }}
            />
            <Button onClick={handleApplyCoupon}>Apply</Button>
          </Stack>
        )}
        {!isReferralCode && !(activeCoupon != null && activePromoCode != null) && referralMessage && (
          <Box sx={{ display: "flex", mt: 0.5 }}>
            <Typography
              variant="caption"
              color="text.secondary"
            >
              {referralMessage}
            </Typography>
          </Box>
        )}
        <Divider />
        <Stack direction="row" alignItems="center">
          <Box sx={{ flex: 8 }} />
          <Typography variant="caption" sx={{ flex: 1 }} align="right">
            Total:
          </Typography>
          <Typography align="right" sx={{ flex: 3 }}>
            <NumericFormat
              value={priceData.total / 100}
              decimalScale={2}
              prefix="$"
              defaultValue="-"
              displayType="text"
              fixedDecimalScale={true}
              thousandSeparator=","
              thousandsGroupStyle="thousand"
            />
          </Typography>
        </Stack>

        <Typography variant="subtitle1">Make Payment</Typography>
        <Typography variant="caption">
          If you select “Credit Card / Electronic Check” you will be directed to
          Stripe’s authentication portal where you will complete your purchase
        </Typography>
        <Typography variant="caption">
          If you select “Pay By Invoice”, you will be asked whether you want to
          add additional recipients to receive the invoice. Following that step,
          you will click “Submit”. Once completed, an invoice will be generated
          and emailed to all the recipients you have identified (it may take a
          few seconds to create the invoice.) While the invoice remains
          outstanding, your Group Plan will be active, however, your group’s
          access may get restricted if payment is not received within 60 days
        </Typography>

        <ToggleButtonGroup
          fullWidth
          value={paymentType}
          onChange={(event, value) => {
            if (value != null) {
              setFieldValue("paymentType", value);
            }
          }}
          exclusive
          name="paymentType"
        >
          <ToggleButton value="direct">
            Credit Card / Electronic Check
          </ToggleButton>
          <ToggleButton value="invoice">Pay by Invoice</ToggleButton>
        </ToggleButtonGroup>
        {some(touched, (property) => {
          if (Array.isArray(property)) {
            return some(property, (item) => some(valuesIn(item), true));
          } else {
            return property;
          }
        }) &&
          !isValid && (
            <Alert severity="error">
              <AlertTitle>Invalid Form Fields Above.</AlertTitle>
            </Alert>
          )}
        <Box
          sx={{
            display: "flex",
            justifyContent: "end",
          }}
        >
          <LoadingButton
            loading={isSubmitting || loading}
            variant="contained"
            type="submit"
          // disabled={!isValid}
          >
            Continue
          </LoadingButton>
        </Box>
      </Stack>
    </Box>
  );
}
